import './Select.scss';
import React, { CSSProperties } from 'react';
import classNames from 'classnames';
import Tooltip from '@/components/basic/Tooltip/Tooltip';
import ButtonIcon from '@/components/basic/ButtonIcon/ButtonIcon';

export type SelectOptionObject = {
    value: string | number;
    label: string;
    disabled?: boolean;
};

export type SelectOption = SelectOptionObject | string | number;

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {
    options: SelectOption[];
    label?: string;
    placeholder?: string; // Acts as the "Please select" non-op
    labelWidth?: string | number | boolean;
    style?: CSSProperties;
    className?: string;
    disabled?: boolean;
    error?: string;
    tooltip?: string;
};

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
    (
        {
            options,
            label,
            placeholder = 'Bitte wählen...',
            className,
            labelWidth,
            style,
            error,
            disabled,
            onChange,
            ...props
        },
        ref,
    ) => {
        const customStyle = {
            ...style,
            ...(labelWidth && labelWidth !== true
                ? {
                      '--label-width':
                          typeof labelWidth === 'number'
                              ? `${labelWidth}px`
                              : labelWidth,
                  }
                : {}),
        } as CSSProperties;

        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            if (onChange) {
                onChange(e);
            }
            e.target.blur();
        };

        const renderSelectBody = (
            <div className="Select_wrapper">
                <select
                    {...props}
                    ref={ref}
                    onChange={handleChange}
                    disabled={disabled}
                    aria-invalid={error ? 'true' : 'false'}
                    className={classNames('Select_input', {
                        isInvalid: !!error,
                        hasPlaceholder: !!placeholder && !props.value,
                    })}
                >
                    {placeholder && (
                        <option value="" disabled hidden={props.required}>
                            {placeholder}
                        </option>
                    )}
                    {options.map((opt, index) => {
                        const isObject =
                            typeof opt === 'object' && opt !== null;

                        const optionValue = isObject
                            ? (opt as SelectOptionObject).value
                            : (opt as string | number);
                        const optionLabel = isObject
                            ? (opt as SelectOptionObject).label
                            : (opt as string | number);
                        const isDisabled = isObject
                            ? (opt as SelectOptionObject).disabled
                            : false;

                        return (
                            <option
                                key={`${optionValue}-${index}`}
                                value={optionValue}
                                disabled={isDisabled}
                            >
                                {optionLabel}
                            </option>
                        );
                    })}
                </select>
                <ButtonIcon
                    icon="chevron"
                    label={`${label || 'Bitte'} auswählen`}
                    className="Select_arrow"
                    aria-hidden="true"
                    tabIndex={-1}
                />
            </div>
        );

        const renderContainer = (
            <label
                className={classNames(
                    'Select_container',
                    {
                        hasLabelWidth: !!labelWidth,
                        isDisabled: disabled,
                    },
                    className,
                )}
                style={customStyle}
            >
                {label && <span className="Select_label">{label}</span>}

                <div className="Select_fieldWrapper">
                    {renderSelectBody}
                    <div
                        className={classNames('Select_error', {
                            isInvalid: !!error,
                        })}
                    >
                        {error || '\u00a0'}
                    </div>
                </div>
            </label>
        );

        return props.tooltip ? (
            <Tooltip content={props.tooltip}>{renderContainer}</Tooltip>
        ) : (
            renderContainer
        );
    },
);

Select.displayName = 'Select';
export default Select;
